export type PwaInstallState = 'unavailable' | 'available' | 'prompting' | 'accepted' | 'installed';
export type PwaUpdateState = 'idle' | 'available' | 'saving' | 'activating' | 'blocked' | 'failed';
export type UpdateBlockReason = 'work' | 'app' | 'form' | 'dialog' | 'busy' | 'signin' | 'save' | 'unresponsive' | 'tabs-changed' | 'unknown';
export type UpdateBlockLocation = 'current' | 'other' | 'unknown';
export interface PwaState {
  install: PwaInstallState;
  update: PwaUpdateState;
  updateBlockReason: UpdateBlockReason | null;
  updateBlockLocation: UpdateBlockLocation | null;
  connection: 'checking' | 'online' | 'offline' | 'unreachable';
  worker: 'unsupported' | 'development' | 'registering' | 'active' | 'failed';
  offlineReady: boolean;
}
export interface PwaController {
  get(): PwaState;
  subscribe(listener: (state: PwaState) => void): () => void;
  requestInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'>;
  checkConnection(): Promise<void>;
  checkUpdate(): Promise<void>;
  applyUpdate(): Promise<boolean>;
  destroy(): void;
}
type InstallPrompt = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
type UpdateGuard = () => boolean | void | UpdateBlockReason | Promise<boolean | void | UpdateBlockReason>;
const blockReasons: readonly string[] = ['work', 'app', 'form', 'dialog', 'busy', 'signin', 'save', 'unresponsive', 'tabs-changed', 'unknown'];
const blockReason = (value: unknown): UpdateBlockReason => typeof value === 'string' && blockReasons.includes(value) ? value as UpdateBlockReason : 'unknown';
const blockLocation = (value: unknown): UpdateBlockLocation => value === 'current' || value === 'other' ? value : 'unknown';
const guards = new Set<UpdateGuard>();
let singleton: PwaController | undefined;

/** A guard must save durable drafts or return false. Nothing is implicitly discarded. */
export function registerUpdateGuard(guard: UpdateGuard): () => void { guards.add(guard); return () => guards.delete(guard); }
export function getPwaController(): PwaController { return singleton ??= createPwaController(); }

/** Use only verified, non-sensitive categories; older tabs can return no reason. */
export function updateBlockMessage(state: PwaState): string {
  const where = state.updateBlockLocation === 'current' ? 'в этом окне' : state.updateBlockLocation === 'other' ? 'в другой вкладке Сот' : 'в открытых окнах Сот';
  switch (state.updateBlockReason) {
    case 'work': return `Сохраните незавершённую работу ${where}, затем нажмите «Повторить».`;
    case 'app': return `Сохраните данные приложения ${where} и закройте его или скройте виджет. Затем нажмите «Повторить».`;
    case 'form': return `Сохраните изменения в форме ${where}, затем нажмите «Повторить».`;
    case 'dialog': return `Сохраните или закройте открытую форму ${where}, затем нажмите «Повторить».`;
    case 'busy': return `Дождитесь завершения операции ${where}, затем нажмите «Повторить».`;
    case 'signin': return `Завершите вход ${where}, затем нажмите «Повторить».`;
    case 'save': return `Не удалось сохранить работу ${where}. Проверьте её и нажмите «Повторить».`;
    case 'unresponsive': return 'Одна из вкладок Сот не ответила. Откройте её, проверьте работу и нажмите «Повторить».';
    case 'tabs-changed': return 'Открытые вкладки изменились во время проверки. Нажмите «Повторить».';
    default: return 'Не удалось проверить открытые окна Сот. Проверьте незавершённую работу и нажмите «Повторить».';
  }
}

export function createPwaController(): PwaController {
  const supported = 'serviceWorker' in navigator, development = import.meta.env.DEV;
  const display = matchMedia('(display-mode: standalone)');
  const installed = () => display.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  let state: PwaState = { install: installed() ? 'installed' : 'unavailable', update: 'idle', updateBlockReason: null, updateBlockLocation: null, connection: 'checking',
    worker: development ? 'development' : supported ? 'registering' : 'unsupported', offlineReady: false };
  let prompt: InstallPrompt | undefined, registration: ServiceWorkerRegistration | undefined, destroyed = false, installGeneration = 0;
  let connectionFlight: Promise<void> | undefined, guardFlight: Promise<UpdateBlockReason | null> | undefined, updateFlight: Promise<boolean> | undefined;
  let reloadArmed = false, reloadRequired = false, reloading = false, lastUpdateCheck = 0;
  let reloadWorker: ServiceWorker | undefined, requestedWorker: ServiceWorker | undefined;
  const listeners = new Set<(state: PwaState) => void>(), abort = new AbortController();
  const publish = (patch: Partial<PwaState>) => {
    if (patch.update && patch.update !== 'blocked') patch = { ...patch, updateBlockReason: null, updateBlockLocation: null };
    if (destroyed || Object.entries(patch).every(([key, value]) => state[key as keyof PwaState] === value)) return;
    state = { ...state, ...patch }; for (const listener of listeners) listener({ ...state });
  };
  const saveDrafts = (): Promise<UpdateBlockReason | null> => guardFlight ??= (async () => {
    try { for (const guard of guards) { const result = await guard(); if (result === false) return 'unknown'; if (typeof result === 'string') return blockReason(result); } return null; }
    catch { return 'save'; }
  })().finally(() => { guardFlight = undefined; });
  const workerRequest = <T>(worker: ServiceWorker, type: string, timeout = 5000): Promise<T> => new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => { channel.port1.close(); reject(new Error('worker_timeout')); }, timeout);
    channel.port1.onmessage = event => { clearTimeout(timer); channel.port1.close(); resolve(event.data as T); };
    try { worker.postMessage({ type }, [channel.port2]); } catch (error) { clearTimeout(timer); channel.port1.close(); reject(error); }
  });
  const refreshOffline = async () => {
    const worker = navigator.serviceWorker?.controller;
    if (!worker || development) return;
    try {
      const response = await workerRequest<{ offlineReady?: boolean }>(worker, 'SOTY_OFFLINE_STATUS');
      publish({ worker: 'active', offlineReady: response.offlineReady === true });
    } catch { publish({ offlineReady: false }); }
  };
  const checkConnection = (): Promise<void> => connectionFlight ??= (async () => {
    try {
      const response = await fetch('/health', { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.any([abort.signal, AbortSignal.timeout(5000)]) });
      const data: unknown = response.ok ? await response.json() : null;
      if (data && typeof data === 'object' && 'ok' in data && data.ok === true) publish({ connection: 'online' });
      else publish({ connection: 'unreachable' });
    } catch { publish({ connection: navigator.onLine ? 'unreachable' : 'offline' }); }
  })().finally(() => { connectionFlight = undefined; });
  const checkUpdate = async () => {
    if (!registration || development || destroyed) return;
    lastUpdateCheck = Date.now();
    try { await registration.update(); if (registration.waiting) publish({ update: 'available' }); }
    catch { /* An update check is independent of editing and connection state. */ }
  };
  const appInstalled = () => { installGeneration++; prompt = undefined; publish({ install: 'installed' }); };
  const beforeInstall = (event: Event) => {
    event.preventDefault();
    if (installed() || state.install === 'installed') { appInstalled(); return; }
    installGeneration++; prompt = event as InstallPrompt; publish({ install: 'available' });
  };
  const settleInstall = (generation: number, next: PwaInstallState) => {
    // The browser's installation event outranks a late result of its old prompt.
    if (destroyed || generation !== installGeneration) return;
    if (installed() || state.install === 'installed') appInstalled();
    else publish({ install: next });
  };
  const displayChanged = () => { if (installed()) appInstalled(); };
  const refreshConnection = () => { void checkConnection(); };
  const visible = () => {
    if (document.visibilityState !== 'visible') return;
    void checkConnection(); void refreshOffline();
    if (Date.now() - lastUpdateCheck > 60 * 60 * 1000) void checkUpdate();
  };
  const rejectRedundant = (worker: ServiceWorker): boolean => {
    if (worker.state !== 'redundant') return false;
    if (reloadWorker === worker) { reloadArmed = false; reloadRequired = false; }
    publish({ update: state.update === 'blocked' ? 'blocked' : registration?.waiting && registration.waiting !== worker ? 'available' : 'failed' });
    return true;
  };
  const reloadWorkerChanged = (event: Event) => {
    const worker = reloadWorker;
    if (!destroyed && !reloading && worker && event.currentTarget === worker) rejectRedundant(worker);
  };
  const observeReloadWorker = (worker: ServiceWorker) => {
    if (reloadWorker === worker) return;
    reloadWorker?.removeEventListener('statechange', reloadWorkerChanged);
    reloadWorker = worker; worker.addEventListener('statechange', reloadWorkerChanged, { signal: abort.signal });
  };
  const message = (event: MessageEvent) => {
    // Only a worker registered at this origin may initiate an update handshake.
    const source = event.source;
    if (!(source instanceof ServiceWorker) || new URL(source.scriptURL).origin !== location.origin) return;
    if (event.data?.type === 'SOTY_PREPARE_UPDATE') {
      const port = event.ports[0]; if (!port) return;
      publish({ update: 'saving' });
      void saveDrafts().then(reason => {
        port.postMessage(reason ? { ready: false, reason } : { ready: true }); port.close();
        publish(reason ? { update: 'blocked', updateBlockReason: reason, updateBlockLocation: 'current' } : { update: 'available' });
      });
    } else if (event.data?.type === 'SOTY_UPDATE_COMMIT') {
      if (reloading || requestedWorker && source !== requestedWorker || source.state === 'redundant') return;
      observeReloadWorker(source);
      reloadArmed = true; reloadRequired = true; publish({ update: 'activating' });
      // The controller can change before this cross-worker message arrives.
      if (navigator.serviceWorker.controller === source) controllerChanged();
    }
  };
  const controllerChanged = async (): Promise<boolean> => {
    void refreshOffline();
    if (destroyed) return false;
    if (reloading) return true;
    const worker = reloadWorker;
    if (!reloadArmed || !worker || rejectRedundant(worker) || navigator.serviceWorker.controller !== worker) return false;
    // Typing may resume between prepare and activation: recheck before navigation.
    const reason = await saveDrafts();
    if (destroyed) return false;
    if (reloading) return true;
    if (!reloadArmed || reloadWorker !== worker || rejectRedundant(worker) || navigator.serviceWorker.controller !== worker) return false;
    if (reason) { reloadArmed = false; publish({ update: 'blocked', updateBlockReason: reason, updateBlockLocation: 'current' }); return false; }
    reloading = true; reloadArmed = false; reloadRequired = false; location.reload(); publish({ update: 'idle' }); return true;
  };
  window.addEventListener('beforeinstallprompt', beforeInstall, { signal: abort.signal });
  window.addEventListener('appinstalled', appInstalled, { signal: abort.signal });
  window.addEventListener('online', refreshConnection, { signal: abort.signal });
  window.addEventListener('offline', refreshConnection, { signal: abort.signal });
  document.addEventListener('visibilitychange', visible, { signal: abort.signal });
  display.addEventListener('change', displayChanged, { signal: abort.signal });
  if (supported) {
    navigator.serviceWorker.addEventListener('message', message, { signal: abort.signal });
    navigator.serviceWorker.addEventListener('controllerchange', controllerChanged, { signal: abort.signal });
    void (async () => {
      try {
        // A Vite worker retires an existing production-preview cache at this origin.
        if (development && !await navigator.serviceWorker.getRegistration('/')) return;
        registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
        if (destroyed || development) return;
        if (registration.waiting) publish({ update: 'available' });
        registration.addEventListener('updatefound', () => {
          const installing = registration?.installing; if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) publish({ update: 'available' });
            if (installing.state === 'activated') void refreshOffline();
          }, { signal: abort.signal });
        }, { signal: abort.signal });
        void navigator.serviceWorker.ready.then(() => { if (!destroyed) void refreshOffline(); });
        void refreshOffline();
      } catch { publish({ worker: 'failed', offlineReady: false }); }
    })();
  }
  const interval = setInterval(() => { if (document.visibilityState === 'visible') void checkConnection(); }, 30_000);
  void checkConnection();
  return {
    get: () => ({ ...state }),
    subscribe(listener) { listeners.add(listener); listener({ ...state }); return () => listeners.delete(listener); },
    async requestInstall() {
      if (destroyed || !prompt || state.install !== 'available') return 'unavailable';
      const event = prompt, generation = installGeneration; prompt = undefined; publish({ install: 'prompting' });
      try { await event.prompt(); const choice = await event.userChoice; settleInstall(generation, choice.outcome === 'accepted' ? 'accepted' : 'unavailable'); return choice.outcome; }
      catch { settleInstall(generation, 'unavailable'); return 'unavailable'; }
    },
    checkConnection, checkUpdate,
    applyUpdate() {
      if (updateFlight) return updateFlight;
      updateFlight = (async () => {
        if (destroyed || development || (!registration?.waiting && !reloadRequired)) return false;
        const worker = registration?.waiting;
        requestedWorker = worker ?? reloadWorker;
        publish({ update: 'saving' });
        if (!worker) {
          if (!reloadWorker || navigator.serviceWorker.controller !== reloadWorker) { publish({ update: 'failed' }); return false; }
          reloadArmed = true; return controllerChanged();
        }
        const reason = await saveDrafts();
        if (reason) { publish({ update: 'blocked', updateBlockReason: reason, updateBlockLocation: 'current' }); return false; }
        if (destroyed) return false;
        if (reloading) return true;
        if (registration?.waiting !== worker) {
          if (reloadRequired && reloadWorker === worker && navigator.serviceWorker.controller === worker) return controllerChanged();
          publish({ update: registration?.waiting ? 'available' : 'failed' }); return false;
        }
        try {
          const result = await workerRequest<{ ready?: boolean; reason?: unknown; location?: unknown }>(worker, 'SOTY_ACTIVATE_UPDATE', 20_000);
          if (destroyed || state.update === 'blocked') return false;
          if (reloading) return true;
          if (rejectRedundant(worker)) return false;
          if (!result.ready) { publish({ update: 'blocked', updateBlockReason: blockReason(result.reason), updateBlockLocation: blockLocation(result.location) }); return false; }
          observeReloadWorker(worker);
          reloadArmed = true; reloadRequired = true; publish({ update: 'activating' });
          if (navigator.serviceWorker.controller === worker) return controllerChanged();
          return true;
        } catch {
          if (destroyed) return false;
          if (reloading) return true;
          // A lost reply cannot undo the same worker's verified commit and save.
          if (reloadRequired && reloadWorker === worker && navigator.serviceWorker.controller === worker) return controllerChanged();
          if (state.update !== 'blocked') publish({ update: 'failed' }); return false;
        }
      })().finally(() => { updateFlight = undefined; requestedWorker = undefined; }); return updateFlight;
    },
    destroy() { destroyed = true; installGeneration++; prompt = undefined; abort.abort(); clearInterval(interval); listeners.clear(); if (singleton) singleton = undefined; },
  };
}

/** Tracks real editing, not programmatic prefill. Domain drafts should use their own durable guard. */
export function watchFormEdits(root: Document | HTMLElement = document): { hasUnsavedChanges(): boolean; markSaved(element?: HTMLElement): void; destroy(): void } {
  const values = new Map<HTMLInputElement | HTMLTextAreaElement, string>(), edited = new Set<HTMLInputElement | HTMLTextAreaElement>();
  const abort = new AbortController();
  const prune = () => { for (const input of values.keys()) if (!input.isConnected) { values.delete(input); edited.delete(input); } };
  const field = (target: EventTarget | null) => target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement && ['text', 'email', 'url', 'tel', 'password', 'number'].includes(target.type) ? target : null;
  const remember = (event: Event) => { prune(); const input = field(event.target); if (input && !input.closest('[data-pwa-ignore]') && !values.has(input)) values.set(input, input.value); };
  const changed = (event: Event) => { const input = field(event.target); if (input && values.has(input)) edited.add(input); };
  root.addEventListener('focusin', remember, { signal: abort.signal }); root.addEventListener('beforeinput', remember, { signal: abort.signal });
  root.addEventListener('input', changed, { signal: abort.signal });
  return {
    hasUnsavedChanges() { prune(); return [...edited].some(input => !input.closest('[data-pwa-ignore]') && input.value !== values.get(input)); },
    markSaved(element) { prune(); for (const input of edited) if (!element || element === input || element.contains(input)) { values.set(input, input.value); edited.delete(input); } },
    destroy() { abort.abort(); values.clear(); edited.clear(); },
  };
}
